import React, { useState, useEffect } from 'react';

const BlogPost = () => {
  // Get slug from URL path
  const [slug, setSlug] = useState('');
  
  useEffect(() => {
    const path = window.location.pathname;
    const extractedSlug = path.replace('/blog/', '').split('?')[0];
    if (extractedSlug) {
      setSlug(extractedSlug);
    }
  }, []);

  const blogPosts = {
    "obituary-writing-resources-guide": {
      title: "Obituary Writing Resources: Examples, Templates & Guides for Every Situation",
      date: "March 31, 2026",
      readTime: "5 min read",
      category: "Resource Guide",
      content: `
        <h2>Your Complete Obituary Writing Resource Library</h2>
        <p>Writing an obituary during a time of grief can feel overwhelming. That's why we've created an extensive collection of guides, examples, and templates to help you through every step of the process — no matter who you're writing for or what tone you need.</p>
        <p>Below you'll find links to each resource, organized by category. Every guide includes real examples, practical tips, and fill-in-the-blank templates you can use immediately.</p>

        <h2>Obituary Examples by Relationship</h2>
        <p>The relationship you had with the deceased shapes the tone and content of the obituary. These guides include 3+ complete example obituaries for each relationship, plus tips specific to each situation.</p>
        <ul>
          <li><strong><a href="/obituary-examples-for-mother">Obituary Examples for a Mother</a></strong> — Warm, formal, and faith-based examples that capture a mother's love, sacrifices, and legacy. Includes tips on what makes a mother's obituary special.</li>
          <li><strong><a href="/obituary-examples-for-father">Obituary Examples for a Father</a></strong> — Examples for military, working-class, and professional fathers. Covers how to honor his career, hobbies, and mentorship.</li>
          <li><strong><a href="/obituary-examples-for-spouse">Obituary Examples for a Spouse</a></strong> — Templates for writing about a husband or wife, including how to balance personal grief with public tribute and share your love story.</li>
          <li><strong><a href="/obituary-examples-for-grandparent">Obituary Examples for a Grandparent</a></strong> — Captures generational legacy with examples for grandmothers and grandfathers, including how to honor decades of family traditions.</li>
        </ul>

        <h2>Templates & Style Guides</h2>
        <p>Not sure where to start? These resources give you a framework to build from.</p>
        <ul>
          <li><strong><a href="/obituary-templates-by-tone">Obituary Templates by Tone</a></strong> — Fill-in-the-blank templates organized by style: simple, heartfelt, religious, celebratory, and formal. Each includes guidance on when to use that tone.</li>
          <li><strong><a href="/free-obituary-template">Free Obituary Template</a></strong> — A complete, ready-to-use template that works for newspapers and online memorials. Includes formatting guidelines and word count recommendations.</li>
          <li><strong><a href="/obituary-generator-by-religion">Obituary Guide for Different Religions & Cultures</a></strong> — Covers Christian, Catholic, Jewish, Islamic, Hindu, Buddhist, and secular traditions with key phrases, customs, and sample openings for each.</li>
        </ul>

        <h2>How-To Guides</h2>
        <p>Practical advice for the writing process itself.</p>
        <ul>
          <li><strong><a href="/obituary-sections-guide">Obituary Sections Guide</a></strong> — A detailed breakdown of every section: opening line, biographical info, life story, family list, service details, and memorial donations. With examples and do's/don'ts for each.</li>
          <li><strong><a href="/how-to-write-an-obituary-in-30-minutes">How to Write an Obituary in 30 Minutes</a></strong> — A timed, step-by-step guide for when the funeral home needs it today. Includes emergency shortcuts and a quick-start checklist.</li>
          <li><strong><a href="/what-not-to-include-in-an-obituary">What Not to Include in an Obituary</a></strong> — Common mistakes to avoid: cause of death debates, family conflicts, financial details, privacy risks, and clichés that weaken the tribute.</li>
        </ul>

        <h2>Need It Done for You?</h2>
        <p>If you'd rather have help, ObitAuthor's AI-powered obituary generator creates a beautiful, personalized obituary in minutes. Just answer a few questions about your loved one and we'll craft a professional tribute that honors their memory.</p>
        <p><a href="/?checkout=true"><strong>Try ObitAuthor now →</strong></a></p>
      `
    },
    "how-to-write-an-obituary-guide": {
      title: "How to Write an Obituary: A Complete Step-by-Step Guide",
      date: "January 20, 2025",
      readTime: "8 min read",
      category: "Writing Guide",
      content: `
        <h2>Introduction</h2>
        <p>Writing an obituary is one of the most important and difficult tasks you'll face after losing a loved one. An obituary serves multiple purposes: it announces the death, provides funeral service information, honors the deceased's life, and helps friends and family begin the grieving process.</p>
        
        <p>This comprehensive guide will walk you through everything you need to know about writing a meaningful obituary, from gathering information to finalizing the text.</p>

        <h2>What is an Obituary?</h2>
        <p>An obituary is a notice of death that's typically published in newspapers, online memorial sites, or funeral programs. Unlike a death notice (which is brief and factual), an obituary tells the story of a person's life, celebrating their achievements, relationships, and impact on others.</p>

        <h2>Step 1: Gather Essential Information</h2>
        <p>Before you begin writing, collect the following information:</p>
        <ul>
          <li><strong>Full name</strong> (including maiden name, if applicable)</li>
          <li><strong>Date and place of birth</strong></li>
          <li><strong>Date and place of death</strong></li>
          <li><strong>Age at time of death</strong></li>
          <li><strong>Cause of death</strong> (optional - only include if family wishes)</li>
          <li><strong>Residence</strong> (city and state)</li>
        </ul>

        <h2>Step 2: Collect Life Details</h2>
        <p>Gather information about their life:</p>
        <ul>
          <li><strong>Education:</strong> Schools attended, degrees earned</li>
          <li><strong>Career:</strong> Jobs, professions, achievements</li>
          <li><strong>Military service:</strong> Branch, rank, years served</li>
          <li><strong>Hobbies and interests:</strong> What they loved doing</li>
          <li><strong>Achievements:</strong> Awards, recognitions, accomplishments</li>
          <li><strong>Organizations:</strong> Clubs, charities, religious affiliations</li>
        </ul>

        <h2>Step 3: List Family Members</h2>
        <p>Include information about surviving and predeceased family:</p>
        <ul>
          <li><strong>Survived by:</strong> Spouse, children, grandchildren, siblings, parents</li>
          <li><strong>Predeceased by:</strong> Family members who died before them</li>
        </ul>
        <p><em>Note: Be sensitive about family dynamics. Include only those relationships the family wants mentioned.</em></p>

        <h2>Step 4: Funeral Service Information</h2>
        <p>Include details about memorial services:</p>
        <ul>
          <li>Date, time, and location of funeral or memorial service</li>
          <li>Visitation/viewing times</li>
          <li>Burial location (if applicable)</li>
          <li>Memorial contributions (charities, organizations)</li>
        </ul>

        <h2>Step 5: Write the Obituary</h2>
        <p>Structure your obituary with these sections:</p>
        
        <h3>Opening</h3>
        <p>Start with the person's full name, age, date of death, and location. Example:</p>
        <p><em>"John Michael Smith, 78, of Bloomington, Indiana, passed away peacefully on January 15, 2025."</em></p>

        <h3>Life Story</h3>
        <p>Write 2-4 paragraphs about their life, focusing on:</p>
        <ul>
          <li>Early life and upbringing</li>
          <li>Education and career</li>
          <li>Personal interests and passions</li>
          <li>Impact on family and community</li>
          <li>Memorable qualities and characteristics</li>
        </ul>

        <h3>Family Section</h3>
        <p>List surviving and predeceased family members. Be consistent with naming conventions.</p>

        <h3>Service Information</h3>
        <p>Provide clear details about memorial services, including date, time, and location.</p>

        <h2>Step 6: Review and Edit</h2>
        <p>Before finalizing:</p>
        <ul>
          <li>Check all dates, names, and facts for accuracy</li>
          <li>Read it aloud to ensure it flows well</li>
          <li>Have another family member review it</li>
          <li>Verify spelling of all names</li>
          <li>Check word count (newspapers often charge by length)</li>
        </ul>

        <h2>Common Mistakes to Avoid</h2>
        <ul>
          <li>Including too much personal information (addresses, phone numbers)</li>
          <li>Making it too long (aim for 200-500 words)</li>
          <li>Using clichés instead of specific details</li>
          <li>Forgetting to include service information</li>
          <li>Not proofreading for errors</li>
        </ul>

        <h2>Professional Help</h2>
        <p>Writing an obituary during a time of grief can be overwhelming. That's where ObitAuthor comes in. Our professional obituary generator helps you create a beautiful, dignified obituary in minutes. Simply answer a few questions about your loved one, and we'll craft a professional obituary that honors their memory.</p>

        <p>At just $9.99 for up to 5 obituaries, ObitAuthor offers significant savings compared to traditional funeral home services, which typically charge $200-$500 or more.</p>

        <h2>Conclusion</h2>
        <p>Writing an obituary is a meaningful way to honor your loved one's life and help others begin the grieving process. Take your time, gather accurate information, and don't hesitate to seek help if you need it. Remember, there's no perfect obituary - what matters most is that it comes from the heart and honors the person you loved.</p>
      `
    },
    "what-to-include-in-obituary": {
      title: "What to Include in an Obituary: Essential Information Checklist",
      date: "January 18, 2025",
      readTime: "6 min read",
      category: "Writing Guide",
      content: `
        <h2>Essential Information Checklist</h2>
        <p>Writing an obituary can feel overwhelming, especially during a time of grief. This checklist will help ensure you include all the important information while creating a meaningful tribute to your loved one.</p>

        <h2>Basic Information (Required)</h2>
        <ul>
          <li>✓ Full legal name (including middle name/maiden name)</li>
          <li>✓ Age at time of death</li>
          <li>✓ Date of death</li>
          <li>✓ Place of death (city and state)</li>
          <li>✓ Date of birth</li>
          <li>✓ Place of birth (city and state)</li>
        </ul>

        <h2>Family Information</h2>
        <ul>
          <li>✓ Surviving spouse (full name)</li>
          <li>✓ Children (names, and spouses if desired)</li>
          <li>✓ Grandchildren (names or count)</li>
          <li>✓ Great-grandchildren (count)</li>
          <li>✓ Siblings (names)</li>
          <li>✓ Parents (if still living)</li>
          <li>✓ Predeceased family members (if relevant)</li>
        </ul>

        <h2>Life Details</h2>
        <ul>
          <li>✓ Education (schools, degrees)</li>
          <li>✓ Career/profession</li>
          <li>✓ Military service (branch, rank, years)</li>
          <li>✓ Hobbies and interests</li>
          <li>✓ Achievements and awards</li>
          <li>✓ Community involvement</li>
          <li>✓ Religious affiliations</li>
          <li>✓ Organizations and memberships</li>
        </ul>

        <h2>Service Information</h2>
        <ul>
          <li>✓ Funeral service date and time</li>
          <li>✓ Funeral service location</li>
          <li>✓ Visitation/viewing times</li>
          <li>✓ Burial location</li>
          <li>✓ Memorial contributions (charities, organizations)</li>
          <li>✓ Funeral home handling arrangements</li>
        </ul>

        <h2>Optional but Meaningful Additions</h2>
        <ul>
          <li>Favorite quotes or sayings</li>
          <li>Personal messages from family</li>
          <li>Special memories or stories</li>
          <li>Photos (for online obituaries)</li>
          <li>Cause of death (only if family wishes to share)</li>
        </ul>

        <h2>What NOT to Include</h2>
        <ul>
          <li>✗ Home addresses</li>
          <li>✗ Phone numbers</li>
          <li>✗ Social Security numbers</li>
          <li>✗ Financial information</li>
          <li>✗ Negative or controversial information</li>
          <li>✗ Cause of death (unless family specifically wants it included)</li>
        </ul>

        <h2>Tips for Gathering Information</h2>
        <p>If you're struggling to remember all the details:</p>
        <ul>
          <li>Ask other family members to help</li>
          <li>Check old photo albums and documents</li>
          <li>Look through their personal papers</li>
          <li>Contact their employer or former employers</li>
          <li>Reach out to friends who may remember details</li>
        </ul>

        <h2>Need Help?</h2>
        <p>If gathering and organizing all this information feels overwhelming, ObitAuthor can help. Our simple form guides you through each section, ensuring you don't miss anything important. We'll help you create a professional, dignified obituary that honors your loved one's memory.</p>
      `
    },
    "funeral-home-obituary-cost": {
      title: "How Much Do Funeral Homes Charge for Obituaries? The Real Cost Breakdown",
      date: "January 12, 2025",
      readTime: "5 min read",
      category: "Cost Comparison",
      content: `
        <h2>The Real Cost of Obituary Writing Services</h2>
        <p>When planning a funeral, families are often surprised by the costs associated with obituary writing. Understanding these costs can help you make informed decisions during a difficult time.</p>

        <h2>What Funeral Homes Typically Charge</h2>
        <p>Funeral homes and crematoriums typically charge <strong>$200-$500 or more</strong> to write an obituary. This cost can vary based on:</p>
        <ul>
          <li>Length of the obituary</li>
          <li>Complexity of the information</li>
          <li>Number of revisions</li>
          <li>Geographic location</li>
          <li>Funeral home policies</li>
        </ul>

        <h2>Why Are Funeral Home Obituaries So Expensive?</h2>
        <p>Several factors contribute to the high cost:</p>
        <ul>
          <li><strong>Time investment:</strong> Writing a quality obituary takes time and expertise</li>
          <li><strong>Staff costs:</strong> Professional writers need to be compensated</li>
          <li><strong>Overhead:</strong> Funeral homes have significant operating costs</li>
          <li><strong>Market positioning:</strong> Families are often willing to pay more during emotional times</li>
        </ul>

        <h2>Additional Costs to Consider</h2>
        <p>Beyond the writing fee, there are often additional costs:</p>
        <ul>
          <li><strong>Newspaper publication:</strong> $50-$500+ depending on length and publication</li>
          <li><strong>Online obituary posting:</strong> $50-$200</li>
          <li><strong>Revisions:</strong> $25-$100 per revision</li>
          <li><strong>Rush service:</strong> Additional fees for same-day or next-day service</li>
        </ul>

        <h2>ObitAuthor: Professional Quality at a Fraction of the Cost</h2>
        <p>At ObitAuthor, we believe that honoring your loved one shouldn't come with an exorbitant price tag. Our service provides:</p>
        
        <h3>What You Get</h3>
        <ul>
          <li>✓ Up to 5 professionally written obituaries</li>
          <li>✓ Instant generation</li>
          <li>✓ Professional formatting</li>
          <li>✓ Print-ready PDF</li>
          <li>✓ No expiration - use codes anytime</li>
          <li>✓ Satisfaction guarantee</li>
        </ul>

        <h3>Cost Comparison</h3>
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <tr style="background: rgba(59, 130, 246, 0.1);">
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd;">Service</th>
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd;">Cost</th>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid #ddd;">Funeral Home Obituary Writing</td>
            <td style="padding: 12px; border: 1px solid #ddd;">$200 - $500+</td>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid #ddd;">ObitAuthor (5 obituaries)</td>
            <td style="padding: 12px; border: 1px solid #ddd;"><strong>$9.99</strong></td>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid #ddd;">Savings with ObitAuthor</td>
            <td style="padding: 12px; border: 1px solid #ddd;"><strong>$190 - $490+</strong></td>
          </tr>
        </table>

        <h2>Quality Comparison</h2>
        <p>You might wonder: "Can a $9.99 service really match the quality of a $500 funeral home obituary?"</p>
        
        <p><strong>Yes.</strong> Here's why:</p>
        <ul>
          <li>Our AI-powered system uses the same professional writing principles</li>
          <li>We focus on dignity, respect, and honoring the deceased</li>
          <li>Each obituary is carefully crafted based on your input</li>
          <li>We offer a satisfaction guarantee - if you're not happy, we'll redo it for free</li>
        </ul>

        <h2>When to Use Each Service</h2>
        <p><strong>Choose ObitAuthor if:</strong></p>
        <ul>
          <li>You want to save money without sacrificing quality</li>
          <li>You prefer to work from home at your own pace</li>
          <li>You need multiple obituaries (for different publications)</li>
          <li>You want instant results</li>
        </ul>

        <p><strong>Consider a funeral home if:</strong></p>
        <ul>
          <li>You need extensive hand-holding and personal assistance</li>
          <li>You want someone else to handle everything</li>
          <li>Cost is not a concern</li>
        </ul>

        <h2>Real Customer Savings</h2>
        <p>Our customers regularly save hundreds of dollars:</p>
        <ul>
          <li>"I was quoted $350 by the funeral home. ObitAuthor gave me the same quality for $9.99." - Sarah M.</li>
          <li>"We needed obituaries for 3 different newspapers. ObitAuthor saved us over $1,000." - Robert T.</li>
        </ul>

        <h2>Conclusion</h2>
        <p>Funeral homes charge $200-$500 or more for obituary writing because they can, not because they must. ObitAuthor proves that professional, dignified obituaries don't have to cost hundreds of dollars.</p>

        <p>During one of life's most difficult times, every dollar saved matters. ObitAuthor helps you honor your loved one with dignity while keeping costs reasonable.</p>

        <p><strong>Ready to save money without sacrificing quality?</strong> <a href="/">Get started with ObitAuthor today</a> for just $9.99.</p>
      `
    }
  };

  // Show loading state while slug is being extracted
  if (!slug) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="glass-card text-center">
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const post = blogPosts[slug];

  if (!post) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="glass-card text-center">
              <h1>Post Not Found</h1>
              <p>The blog post "{slug}" doesn't exist.</p>
              <button 
                onClick={() => window.location.href = '/blog'}
                className="btn btn-primary"
              >
                Back to Blog
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <button 
            onClick={() => window.location.href = '/blog'}
            className="btn btn-outline-primary mb-4"
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Blog
          </button>

          <div className="glass-card">
            <div className="mb-4">
              <span className="badge" style={{ 
                background: 'rgba(59, 130, 246, 0.2)', 
                color: '#1e40af',
                padding: '0.5rem 1rem',
                borderRadius: '12px',
                fontSize: '0.9rem',
                marginBottom: '1rem',
                display: 'inline-block'
              }}>
                {post.category}
              </span>
              <h1 className="commercial-heading mb-3">{post.title}</h1>
              <div className="d-flex gap-4 mb-4" style={{ color: '#666', fontSize: '0.95rem' }}>
                <span>{post.date}</span>
                <span>{post.readTime}</span>
              </div>
              
              {/* Social Sharing Buttons */}
              <div className="d-flex gap-2 flex-wrap mb-4">
                <span style={{ color: '#4a5568', fontWeight: '500', marginRight: '0.5rem', alignSelf: 'center' }}>Share:</span>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://obitauthor.com/blog/${slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-share-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(59, 89, 152, 0.1)',
                    color: '#3b5998',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  title="Share on Facebook"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(`https://obitauthor.com/blog/${slug}`)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-share-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(29, 161, 242, 0.1)',
                    color: '#1da1f2',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  title="Share on Twitter"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://obitauthor.com/blog/${slug}`)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-share-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(0, 119, 181, 0.1)',
                    color: '#0077b5',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  title="Share on LinkedIn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`https://obitauthor.com/blog/${slug}`);
                    alert('Link copied to clipboard!');
                  }}
                  className="social-share-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(212, 175, 55, 0.1)',
                    color: '#d4af37',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  title="Copy link"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </button>
              </div>
            </div>

            <div 
              className="blog-content"
              style={{ 
                color: '#000000',
                lineHeight: '1.8',
                fontSize: '1.1rem'
              }}
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            <div className="mt-5 pt-4 border-top text-center">
              <h3 className="mb-3">Need Help Writing an Obituary?</h3>
              <p className="mb-4">Let ObitAuthor help you create a beautiful, professional obituary in minutes.</p>
              <button 
                onClick={() => window.location.href = '/'}
                className="btn btn-primary btn-lg"
              >
                Get Started - Just $9.99
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;

